import React, { useEffect, useState } from "react"
import { FieldError, UseFormRegister, UseFormSetValue } from "react-hook-form"
import TextField from "@mui/material/TextField"
import { styled, Theme } from "@mui/material/styles"
import InputAdornment from "@mui/material/InputAdornment"
import IconButton from "@mui/material/IconButton"
import { MEDIUM } from "constants/index"
import { Size } from "types/general.types"

type InputType = "text" | "number" | "password" | "date" | "email" | "url";
type Direction = "rtl" | "ltr";
type Alignment = "right" | "center" | "left";

type TextFieldProps = {
  id: string;
  label: string;
  defaultValue?: string;
  type?: InputType;
  direction?: Direction;
  alignment?: Alignment;
  pattern?: string;
  floatingLabel?: string;
  placeholder?: string;
  startAdornmentIcon?: React.ReactNode;
  endAdornmentIcon?: React.ReactNode;
  mandatoryIcon?: boolean;
  required?: boolean;
  disabled?: boolean;
  register: UseFormRegister<any>;
  error: FieldError | undefined;
  setValue: UseFormSetValue<any>;
  size?: Size;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void | string | number;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onIconClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export default function CustomTextField(props: TextFieldProps) {
  const {
    id,
    label,
    defaultValue,
    placeholder,
    startAdornmentIcon,
    endAdornmentIcon,
    mandatoryIcon,
    type = "text",
    direction,
    alignment,
    pattern,
    required = false,
    disabled = false,
    register,
    error,
    setValue,
    size = MEDIUM,
    onChange = new Function(),
    onFocus,
    onIconClick
  } = props

  const [localValue, setLocalValue] = useState("")

  useEffect(() => {
    if (defaultValue === localValue) return
    setLocalValue(defaultValue || "")
    setValue(id, defaultValue, { shouldDirty: true })
  }, [defaultValue])

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const cleanedVal = onChange(event)
    const val = cleanedVal?.length ? cleanedVal : event.target.value
    setLocalValue(val)
    setValue(id, val, { shouldDirty: true })
  }

  return (
    <StyledTextField
      id={id}
      value={localValue}
      label={label}
      placeholder={placeholder}
      type={type}
      required={required}
      disabled={disabled}
      variant="outlined"
      {...register(id)}
      direction={direction}
      alignment={alignment}
      haserror={error ? 1 : 0}
      hasstartadornmenticon={startAdornmentIcon ? 1 : 0}
      size={size}
      inputProps={{
        pattern: pattern
      }}
      InputProps={{
        startAdornment: startAdornmentIcon ? <InputAdornment
          className={mandatoryIcon ? "mandatory" : "not-mandatory"}
          position="end"
          onMouseDown={onIconClick}
        >
          <IconButton edge="end">
            {startAdornmentIcon}
          </IconButton>
        </InputAdornment> : <></>,
        endAdornment: endAdornmentIcon ? <InputAdornment
          className={mandatoryIcon ? "mandatory" : "not-mandatory"}
          position="end"
          onMouseDown={onIconClick}
        >
          <IconButton edge="end">
            {endAdornmentIcon}
          </IconButton>
        </InputAdornment> : <></>
      }}
      InputLabelProps={{
        shrink: true
      }}
      onChange={handleInputChange}
      onFocus={onFocus}
    />
  )
}

type StyledTextFieldProps = {
  theme?: Theme;
  haserror: 1 | 0;
  hasstartadornmenticon: 1 | 0;
  type: InputType;
  direction?: Direction;
  alignment?: Alignment;
  size?: Size;
}

const StyledTextField = styled(TextField)(({
                                             theme,
                                             haserror,
                                             hasstartadornmenticon,
                                             type,
                                             direction,
                                             alignment,
                                             size
                                           }: StyledTextFieldProps) => ({
  width: "100%",
  marginBottom: "4.4rem",

  "& .MuiInputBase-root": {
    marginTop: 0,
    padding: hasstartadornmenticon ? "0 8px 0 0" : "0 8px 0 8px",
    border: haserror === 1 ? "1px solid red" : (size === MEDIUM ? `1px solid ${theme?.palette.gray["8"]}` : `1px solid ${theme?.palette.gray["4"]}`),
    borderRadius: "25px",
    backgroundColor: "#FFFFFF",

    "& input": {
      padding: size === MEDIUM ? "24px 10px" : "20px 10px",
      fontSize: "1.2rem",
      color: theme?.palette.gray["14"],
      direction: direction ? direction : (["text", "number"].includes(type) ? "ltr" : "rtl"),
      textAlign: alignment ? alignment : "unset"
    },

    "& .MuiInputAdornment-root.not-mandatory": {
      display: "none"
    },

    "& fieldset": {
      display: "none"
    },

    "& svg": {
      width: 24,
      height: 24,
      stroke: theme?.palette.gray["14"]
    },

    "& .MuiIconButton-edgeEnd:hover": {
      backgroundColor: "transparent !important"
    }
  },

  "& .MuiInputBase-root.Mui-focused": {
    border: `1px solid ${theme?.palette.primary["5"]}`,

    "& .MuiInputAdornment-root.not-mandatory": {
      display: "flex"
    }
  },

  "& label": {
    fontSize: "1.2rem",
    fontWeight: 300,
    transform: "translate(18px, -22px) scale(1)",
    color: haserror === 1 ? "red" : theme?.palette.gray["12"],
    zIndex: 0,

    "&.Mui-focused": {
      fontWeight: 400,
      color: theme?.palette.gray["12"]
    },

    "& .MuiFormLabel-asterisk": {
      color: theme?.palette.error["4"],
      fontWeight: "normal !important"
    }
  }
}))
